// Müşteriler sekmesi — "Havuzdaki Müşteriler": arama + frekans/gün filtreleri,
// Liste/Harita görünümü ve HEP AÇIK yan "Toplu Atama" paneli (GEREKSİNİM 164):
//   • Tablodan müşteri(ler) seç, yandan gün(ler) seç → "Ata" (çoklu-gün, mevcutları korur).
//   • "Kaydır": bir günün müşterilerini başka güne taşı (seçim varsa yalnız seçililer,
//     yoksa kaynak gündeki tümü).
//   • "Günden Çıkar": seçili müşterilerin gün atamalarını kaldır.
// Tek-gün müşteriler satır içi gün/frekans select'i ile hızlı düzenlenir; çoklu-gün
// müşteriler gün rozetleriyle gösterilir ve yan panelden yönetilir.
//
// #189 TASLAK MODELİ: Bu sekmedeki ekleme/çıkarma/program değişiklikleri artık backend'e
// ANINDA yazılmaz; parent'taki useRoutePlanCustomers hook'unda tamponlanır (customers prop).
// Yalnız üstteki "Değişiklikleri Kaydet" bunları yazar, "Vazgeç" geri alır.
import { useState, useMemo } from 'react';
import { s } from '../listPageStyles';
import {
  FREQUENCY_OPTIONS, DAY_OPTIONS,
  frequencyLabel, dayLabel, dayShort, dayColor,
} from './routePlanUtils';
import RouteMap, { DayLegend } from './RouteMap';
import AddCustomersModal from './AddCustomersModal';
import VisitOrderModal from './VisitOrderModal';

// Çalışma satırının atandığı TÜM günler (1=Pzt..7=Paz), sıralı.
const rowDays = (r) => (Array.isArray(r.dayNos) ? r.dayNos : []);

export default function CustomersTab({ planId, isEdit, showToast, customers, vendorIds }) {
  const [search, setSearch] = useState('');
  const [freqFilter, setFreqFilter] = useState('');
  const [dayFilter, setDayFilter] = useState('');
  const [view, setView] = useState('list');
  const [showAdd, setShowAdd] = useState(false);
  const [orderDay, setOrderDay] = useState(null);
  // Toplu seçim + toplu işlem (satır anahtarı _key ile).
  const [selected, setSelected] = useState(() => new Set());
  const [assignDays, setAssignDays] = useState(() => new Set()); // "Ata" hedef gün(ler)
  const [assignFreq, setAssignFreq] = useState(''); // "Ata"da uygulanacak frekans ('' = koru)
  const [shiftFrom, setShiftFrom] = useState(''); // "Kaydır" kaynak gün
  const [shiftTo, setShiftTo] = useState(''); // "Kaydır" hedef gün
  const [removeDay, setRemoveDay] = useState(''); // #799.2 "Günden Çıkar" hedef gün ('' = tüm günler)

  const loading = customers.loading;
  const pending = customers.pending;
  // Görüntülenen satırlar: çıkarılmak üzere işaretlenenler gizlenir.
  const activeRows = useMemo(() => customers.rows.filter((r) => !r._removed), [customers.rows]);

  // #805 KAPSAM DIŞI MÜŞTERİ ÖZETİ.
  //
  // Havuz eklerken kapsamla daraltılıyor, ama kapsam SONRADAN değiştirilebiliyor (Genel sekmesinde
  // distribütör Trabzon'dan Bursa'ya çevrilince 64 Trabzon müşterisi olduğu yerde kalıyordu).
  // Uyumsuzluk kaydetmeyi ENGELLER (RoutePlanDetailPage + sunucu kapısı), burada ise DURUM
  // olarak görünür: kullanıcı kaydete basmadan önce neyi düzelteceğini bilsin.
  // Kapsam boşsa ("tüm distribütörler") kapsam dışı diye bir şey yoktur.
  // #808 Kapsam dışı satırların KENDİSİ de lazım: yalnız sayı göstermek yetmiyordu,
  // kullanıcı 60 müşteri içinden hangilerinin kapsam dışı olduğunu tek tek arıyordu.
  const outsideScopeRows = useMemo(() => {
    const scope = new Set((vendorIds || []).filter(Boolean).map(String));
    if (scope.size === 0) return [];
    return activeRows.filter((r) => r.vendorId && !scope.has(String(r.vendorId)));
  }, [activeRows, vendorIds]);

  // Distribütör adına göre özet: [[ad, adet], …] — uyarı şeridinin metni.
  const outsideScope = useMemo(() => {
    const byVendor = new Map();
    outsideScopeRows.forEach((r) => {
      const label = r.vendorName || 'Tanımsız distribütör';
      byVendor.set(label, (byVendor.get(label) || 0) + 1);
    });
    return [...byVendor.entries()].sort((a, b) => b[1] - a[1]);
  }, [outsideScopeRows]);

  // Kapsam dışı müşteri anahtarları — satır vurgusu ve toplu silme için.
  const outsideScopeKeys = useMemo(
    () => new Set(outsideScopeRows.map((r) => r._key)),
    [outsideScopeRows],
  );

  /**
   * #808 "Müşteri Sil": kapsam dışı TÜM müşterileri ruttan çıkarır — güne bakmaksızın,
   * seçim yapmaya gerek kalmadan. Diğer toplu işlemler gibi TASLAKTIR (#189): "Değişiklikleri
   * Kaydet"e kadar backend'e yazılmaz, "Vazgeç" geri alır — onay metni bunu açıkça söylüyor
   * ki kullanıcı sildim sanıp kaydetmeden çıkmasın.
   */
  const runRemoveOutsideScope = () => {
    const count = outsideScopeRows.length;
    if (count === 0) return;
    const ok = window.confirm(
      `Distribütör kapsamı dışındaki ${count} müşteri ruttan çıkarılacak (gün ataması olsun olmasın).\n\n`
      + 'Bu işlem hemen yazılmaz: üstteki "Değişiklikleri Kaydet" ile kalıcı olur, '
      + '"Vazgeç" ile geri alınır.\n\nDevam edilsin mi?',
    );
    if (!ok) return;
    const keys = outsideScopeRows.map((r) => r._key);
    customers.removeRows(keys);
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.delete(k));
      return next;
    });
    showToast?.('success', `${count} kapsam dışı müşteri ruttan çıkarıldı (kaydedilmeyi bekliyor).`);
  };

  // Satır içi tek-gün düzenleme → taslak program değişimi.
  const updateSchedule = (key, patch) => {
    if (patch.frequency !== undefined) customers.setSchedule(key, { frequency: patch.frequency });
    if (patch.dayNo !== undefined) customers.setSchedule(key, { dayNos: patch.dayNo ? [Number(patch.dayNo)] : [] });
  };

  const removeCustomer = (key) => customers.removeRow(key);

  const toggleRow = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // "Ata" (çoklu-gün, additive): seçili müşterileri seçili günlere ekler (mevcutları korur).
  const runAssign = () => {
    if (selected.size === 0) { showToast?.('error', 'Önce müşteri seçin.'); return; }
    customers.bulkReschedule({
      keys: [...selected],
      toDayNos: [...assignDays].sort((a, b) => a - b),
      frequency: assignFreq || null,
      additive: true,
    });
    setSelected(new Set());
    setAssignDays(new Set());
  };

  // "Kaydır": seçim ZORUNLU DEĞİL. Seçim varsa yalnız seçililerden kaynak güne atanmışlar;
  // seçim yoksa kaynak gündeki TÜM müşteriler hedef güne taşınır (eski gün silinir).
  const runShift = () => {
    const from = Number(shiftFrom);
    const to = Number(shiftTo);
    const keys = selected.size > 0
      ? [...selected]
      : activeRows.filter((r) => rowDays(r).includes(from)).map((r) => r._key);
    if (keys.length === 0) { showToast?.('error', 'Kaynak güne atanmış müşteri yok.'); return; }
    customers.bulkReschedule({ keys, fromDayNo: from, toDayNos: [to], additive: false });
    setSelected(new Set());
  };

  // #799.1 Tek satırda, tek günden çıkarma (gün rozetindeki ✕). Diğer günler korunur.
  const removeDayFromRow = (key, day) => {
    const row = activeRows.find((r) => r._key === key);
    if (!row) return;
    customers.setSchedule(key, { dayNos: rowDays(row).filter((d) => d !== day) });
  };

  /**
   * "Günden Çıkar": seçili müşterileri gün atamasından düşürür (havuzda kalır).
   *
   * #799.2 Artık HANGİ gün olduğu seçilebilir. Eskiden düğme koşulsuz TÜM günleri siliyordu;
   * Pzt+Cuma'ya atanmış 10 müşteriyi yalnız Cuma'dan çıkarmak imkânsızdı. `removeDay` boşsa
   * ("Tüm günler") eski davranış korunur — mevcut kullanım kırılmaz.
   */
  const runRemove = () => {
    if (selected.size === 0) { showToast?.('error', 'Önce müşteri seçin.'); return; }
    if (!removeDayEff) {
      customers.bulkReschedule({ keys: [...selected], toDayNos: [], additive: false });
      setSelected(new Set());
      return;
    }
    const day = Number(removeDayEff);
    let touched = 0;
    selected.forEach((key) => {
      const row = activeRows.find((r) => r._key === key);
      if (!row || !rowDays(row).includes(day)) return;
      customers.setSchedule(key, { dayNos: rowDays(row).filter((d) => d !== day) });
      touched += 1;
    });
    if (touched === 0) {
      showToast?.('error', `Seçili müşterilerin hiçbiri ${dayLabel(day)} gününe atanmamış.`);
      return;
    }
    showToast?.('success', `${touched} müşteri ${dayLabel(day)} gününden çıkarıldı. Kaydetmek için üstten "Değişiklikleri Kaydet".`);
    setSelected(new Set());
    setRemoveDay('');
  };

  // Talep 904 — "Havuzdan Çıkar": seçili müşterileri ruttan TAMAMEN çıkarır.
  // "Günden Çıkar"dan farkı: o yalnız gün atamasını siler, müşteri havuzda kalır.
  // Bu düğme müşterinin gün ataması olsun olmasın çalışır (havuza eklenip hiçbir güne
  // atanmamış müşteriler eskiden yalnız satır satır × ile çıkarılabiliyordu).
  const runRemoveFromPool = () => {
    if (selected.size === 0) { showToast?.('error', 'Önce müşteri seçin.'); return; }
    const count = selected.size;
    selected.forEach((key) => customers.removeRow(key));
    setSelected(new Set());
    showToast?.('success', `${count} müşteri havuzdan çıkarıldı. Kaydetmek için üstten "Değişiklikleri Kaydet".`);
  };

  const toggleAssignDay = (val) => setAssignDays((prev) => {
    const next = new Set(prev);
    if (next.has(val)) next.delete(val); else next.add(val);
    return next;
  });

  // AddCustomersModal'dan dönen seçimi TASLAK olarak havuza ekle (backend'e yazmaz).
  const handleStage = (poolRows, frequency, days, city) => {
    customers.addCustomers(poolRows, frequency, days, city);
    const dayLabels = days.length
      ? ` (${days.map((d) => DAY_OPTIONS.find((o) => o.value === d)?.short || d).join(', ')})`
      : '';
    showToast?.('success', `${poolRows.length} müşteri eklendi${dayLabels}. Kaydetmek için üstten "Değişiklikleri Kaydet".`);
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    const df = dayFilter ? Number(dayFilter) : null;
    return activeRows.filter((r) => {
      if (freqFilter && (r.frequency ?? null) !== freqFilter) return false;
      if (df && !rowDays(r).includes(df)) return false;
      if (term) {
        const hay = `${r.customerTextId || ''} ${r.customerTitle || ''} ${r.address || ''}`.toLocaleLowerCase('tr-TR');
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [activeRows, search, freqFilter, dayFilter]);

  // #799.2 Seçili müşterilerin atandığı günlerin birleşimi — "Günden Çıkar" seçeneklerini besler.
  // Seçimde olmayan bir günü listelemek kullanıcıya hiçbir şey yapmayan bir seçenek sunmak olurdu.
  const selectedDayOptions = useMemo(() => {
    const set = new Set();
    activeRows.forEach((r) => { if (selected.has(r._key)) rowDays(r).forEach((d) => set.add(d)); });
    return [...set].sort((a, b) => a - b);
  }, [activeRows, selected]);

  // Seçim değişince eski gün tercihi geçersizleşebilir (o gün artık hiçbir seçilide yok).
  // State'i render içinde düzeltmek yerine TÜRETİLMİŞ değer kullanılır: "Tüm günler"e düşer.
  const removeDayEff = removeDay && selectedDayOptions.includes(Number(removeDay)) ? removeDay : '';

  const filteredKeys = useMemo(() => filtered.map((r) => r._key), [filtered]);
  const selectedVisible = useMemo(() => filteredKeys.filter((k) => selected.has(k)), [filteredKeys, selected]);
  const allFilteredSelected = filteredKeys.length > 0 && selectedVisible.length === filteredKeys.length;
  const toggleAllFiltered = () => setSelected((prev) => {
    if (allFilteredSelected) {
      const next = new Set(prev);
      filteredKeys.forEach((k) => next.delete(k));
      return next;
    }
    return new Set([...prev, ...filteredKeys]);
  });

  const mapPoints = useMemo(() => filtered
    .map((r) => {
      const days = rowDays(r);
      return {
        lat: Number(r.latitude),
        lng: Number(r.longitude),
        color: dayColor(days[0]),
        label: r.customerTitle,
        code: r.customerTextId,
      };
    })
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(Math.abs(p.lat) < 0.001 && Math.abs(p.lng) < 0.001)), [filtered]);

  // Haritada/listede görünen günlerin legend'i (çoklu-gün dahil).
  const legendDays = useMemo(() => {
    const present = new Set();
    filtered.forEach((r) => rowDays(r).forEach((d) => present.add(d)));
    return DAY_OPTIONS.filter((d) => present.has(d.value));
  }, [filtered]);

  // Talep 903: Modal artık KRİTERSİZ açılır — İl ön-seçimi kaldırıldı (eskiden havuzdaki
  // ilk müşterinin ili ön-seçiliyordu, Talep 810). Ekran açıldığında ilk sekmedeki
  // distribütörün (yoksa kullanıcının yetkili olduğu distribütörlerin) aktif müşterileri
  // alfabetik listelenir; kullanıcı isterse İl filtresini kendisi seçer.

  // AddCustomersModal'a "zaten planda" tespiti için aktif müşteri id kümesi (taslak dahil).
  const stagedCustomerIds = useMemo(
    () => new Set(activeRows.map((r) => String(r.customerId))), [activeRows]);

  const orderCustomers = useMemo(
    () => (orderDay ? activeRows.filter((r) => rowDays(r).includes(orderDay))
      .sort((a, b) => (a.visitOrderNo || 0) - (b.visitOrderNo || 0)) : []),
    [orderDay, activeRows],
  );

  const openOrderModal = () => {
    // Ziyaret sırası sunucudaki kayıtlı müşterilere (rpcId) göre yazılır; bekleyen taslak
    // değişiklik varken sıralama yapılamaz — önce kaydedilmeli.
    if (pending.total > 0) { showToast?.('error', 'Ziyaret sırası için önce değişiklikleri kaydedin.'); return; }
    if (!dayFilter) { showToast?.('error', 'Sıralama için önce bir gün filtresi seçin.'); return; }
    setOrderDay(Number(dayFilter));
  };

  // Tablo sütun sayısı: 5 sabit + (düzenlemede: checkbox + sil).
  const colCount = 5 + (isEdit ? 2 : 0);
  const showPanel = isEdit && view === 'list';

  return (
    <div>
      {/* #189 Bekleyen (kaydedilmemiş) taslak değişiklik bilgi şeridi */}
      {isEdit && pending.total > 0 && (
        <div style={pendingBanner}>
          <span style={{ fontWeight: 800 }}>● {pending.total} kaydedilmemiş değişiklik</span>
          <span>
            {pending.adds ? `+${pending.adds} ekleme  ` : ''}
            {pending.removes ? `−${pending.removes} çıkarma  ` : ''}
            {pending.changes ? `~${pending.changes} program` : ''}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 12 }}>
            Üstteki <b>“Değişiklikleri Kaydet”</b> ile yazın, <b>“Vazgeç”</b> ile geri alın.
          </span>
        </div>
      )}

      {/* #805 + #808 Kapsam dışı müşteri uyarısı — ARTIK SALT BİLGİ DEĞİL, iki yönüyle de:
          • #805: bu durumda KAYDETME REDDEDİLİR (RoutePlanDetailPage.assertVendorConsistency
            ve sunucuda RoutePlansController.Update). Şerit, kaydete basmadan önce neyin
            düzeltilmesi gerektiğini söyler.
          • #808: düzenleme modunda kapsam dışı müşteriler tek düğmeyle toplu temizlenebilir
            (tespit etmek elle çok zordu). Yani şerit hem engelin sebebini hem çözümünü taşır. */}
      {outsideScope.length > 0 && (
        <div style={scopeWarnBanner}>
          <span style={{ fontWeight: 800 }}>⛔ Distribütör kapsamı dışında müşteri var — kayıt yapılamaz</span>
          <span>{outsideScope.map(([name, count]) => `${name} (${count} müşteri)`).join(' · ')}</span>
          {isEdit && (
            <button
              type="button"
              style={scopeWarnBtn}
              onClick={runRemoveOutsideScope}
              title="Kapsam dışındaki tüm müşterileri ruttan çıkarır (gün ataması olsun olmasın). Taslaktır; Değişiklikleri Kaydet ile yazılır."
            >
              Müşteri Sil ({outsideScopeRows.length})
            </button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12 }}>
            <b>Genel</b> sekmesindeki distribütör seçimini düzeltin <b>veya</b> bu müşterileri havuzdan çıkarın.
          </span>
        </div>
      )}

      {/* Başlık şeridi */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Havuzdaki Müşteriler</h3>
        <span style={countPill}>{activeRows.length} müşteri</span>
        <div style={{ marginLeft: 'auto', position: 'relative', minWidth: 200 }}>
          <input style={{ ...s.input, width: '100%' }} placeholder="Havuzda ara…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Filtreler + görünüm + ekle */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <select style={{ ...s.input, minWidth: 160 }} value={freqFilter} onChange={(e) => setFreqFilter(e.target.value)}>
          <option value="">Tüm frekanslar</option>
          {FREQUENCY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select style={{ ...s.input, minWidth: 140 }} value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}>
          <option value="">Tüm günler</option>
          {DAY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        <div style={{ display: 'inline-flex', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-color,#e5e7eb)' }}>
          <button onClick={() => setView('list')} style={viewBtn(view === 'list')}>☰ Liste</button>
          <button onClick={() => setView('map')} style={viewBtn(view === 'map')}>🗺 Harita</button>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {isEdit && <button style={s.btnSecondary} onClick={openOrderModal} title="Seçili günün ziyaret sırasını düzenle">↕ Ziyaret Sırası</button>}
          {isEdit && <button style={s.btnPrimary} onClick={() => setShowAdd(true)}>+ Müşteri Ekle</button>}
        </div>
      </div>

      {/* Gövde: HEP AÇIK yan panel (sol) + tablo (sağ). Harita/okuma modunda tam genişlik. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {showPanel && (
          <aside style={sidePanel}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>Toplu Atama</span>
              <span style={selected.size > 0 ? bulkCount : bulkCountEmpty}>
                {selected.size > 0 ? `${selected.size} seçili` : 'müşteri seçin'}
              </span>
            </div>
            <p style={panelHint}>Tablodan müşteri(leri) seçin, aşağıdan günleri seçip <b>Ata</b> deyin. Bir müşteri birden çok güne atanabilir.</p>

            {/* 1) Günlere Ata (çoklu-gün) */}
            <div style={panelSection}>
              <span style={panelLabel}>Günlere ata</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {DAY_OPTIONS.map((d) => {
                  const on = assignDays.has(d.value);
                  return (
                    <button key={d.value} type="button" title={d.label}
                      onClick={() => toggleAssignDay(d.value)} style={dayChip(on, d.color)}>
                      {d.short}
                    </button>
                  );
                })}
              </div>
              <select
                style={{ ...s.input, padding: '7px 9px', marginTop: 8, width: '100%' }}
                value={assignFreq} onChange={(e) => setAssignFreq(e.target.value)}
                title="Atamada uygulanacak ziyaret frekansı"
              >
                <option value="">Frekans: müşterininki korunsun</option>
                {FREQUENCY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <button
                style={{ ...s.btnPrimary, width: '100%', marginTop: 8, opacity: (assignDays.size === 0 || selected.size === 0) ? 0.55 : 1 }}
                disabled={assignDays.size === 0 || selected.size === 0}
                onClick={runAssign}
              >
                {assignDays.size > 0 ? `Ata (${assignDays.size} gün)` : 'Ata'}
              </button>
            </div>

            {/* 2) Güne Kaydır (taşı) */}
            <div style={panelSection}>
              <span style={panelLabel}>Güne kaydır (taşır)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <select style={{ ...s.input, padding: '7px 8px', flex: 1 }} value={shiftFrom} onChange={(e) => setShiftFrom(e.target.value)}>
                  <option value="">Şu günden</option>
                  {DAY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                <span style={{ color: '#94a3b8' }}>→</span>
                <select style={{ ...s.input, padding: '7px 8px', flex: 1 }} value={shiftTo} onChange={(e) => setShiftTo(e.target.value)}>
                  <option value="">Bu güne</option>
                  {DAY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <button
                style={{ ...s.btnSecondary, width: '100%', marginTop: 8, opacity: (!shiftFrom || !shiftTo) ? 0.55 : 1 }}
                disabled={!shiftFrom || !shiftTo}
                onClick={runShift}
                title="Müşteri seçtiyseniz yalnız seçtikleriniz, seçim yoksa kaynak gündeki TÜM müşteriler hedef güne kaydırılır. Kaydırma eski günü siler."
              >
                Kaydır
              </button>
            </div>

            {/* 3) Günden çıkar / Havuzdan Çıkar (Talep 904) + seçimi temizle */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              {/* #799.2 HANGİ gün: seçenekler yalnız SEÇİLİ müşterilerin gerçekten atandığı günler.
                  Boş seçim ("Tüm günler") eski davranıştır. */}
              <select
                style={{ ...inlineSelect, width: '100%' }}
                value={removeDayEff}
                onChange={(e) => setRemoveDay(e.target.value)}
                disabled={selected.size === 0}
                title="Seçili müşteriler hangi günden çıkarılacak?"
              >
                <option value="">Tüm günlerden çıkar</option>
                {selectedDayOptions.map((d) => (
                  <option key={d} value={d}>{dayLabel(d)} gününden çıkar</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={{ ...s.btnSecondary, flex: 1, padding: '7px 10px', color: 'var(--danger)', borderColor: 'rgba(226,68,92,0.4)', opacity: selected.size === 0 ? 0.55 : 1 }}
                  disabled={selected.size === 0}
                  onClick={runRemove}
                  title={removeDayEff
                    ? `Seçili müşterileri yalnız ${dayLabel(Number(removeDayEff))} gününden çıkarır; diğer günleri korur`
                    : 'Seçili müşterilerin TÜM gün atamalarını kaldırır (havuzda kalır)'}
                >
                  {removeDayEff ? `${dayLabel(Number(removeDayEff))} gününden çıkar` : 'Günden çıkar'}
                </button>
                <button style={{ ...s.btnSecondary, flex: 1, padding: '7px 10px' }} disabled={selected.size === 0} onClick={() => setSelected(new Set())}>
                  Seçimi temizle
                </button>
              </div>
              {/* Talep 904: gün ataması olsun olmasın, seçilenleri ruttan tamamen çıkarır. */}
              <button
                style={{
                  ...s.btnSecondary, width: '100%', padding: '7px 10px', fontWeight: 700,
                  color: 'var(--danger)', borderColor: 'rgba(226,68,92,0.55)',
                  background: 'rgba(226,68,92,0.06)',
                  opacity: selected.size === 0 ? 0.55 : 1,
                }}
                disabled={selected.size === 0}
                onClick={runRemoveFromPool}
                title="Seçili müşterileri rut havuzundan tamamen çıkarır (gün ataması olsun olmasın)"
              >
                {selected.size > 0 ? `Havuzdan Çıkar (${selected.size})` : 'Havuzdan Çıkar'}
              </button>
            </div>
          </aside>
        )}

        <div style={{ flex: 1, minWidth: 320 }}>
          {view === 'map' ? (
            <>
              <RouteMap points={mapPoints} height={420} />
              <DayLegend days={legendDays} />
            </>
          ) : (
            <div style={{ border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {isEdit && (
                      <th style={cellHead(40)}>
                        <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} title="Görünenleri seç" />
                      </th>
                    )}
                    <th style={cellHead()}>KOD</th>
                    <th style={cellHead()}>MÜŞTERİ</th>
                    <th style={cellHead()}>BÖLGE</th>
                    <th style={cellHead(150)}>FREKANS</th>
                    <th style={cellHead(170)}>GÜN(LER)</th>
                    {isEdit && <th style={cellHead(60)}></th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={colCount} style={{ ...cell(), textAlign: 'center', color: '#9ca3af' }}>Yükleniyor…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={colCount} style={{ ...cell(), textAlign: 'center', color: '#9ca3af' }}>Havuzda müşteri yok</td></tr>
                  ) : filtered.map((r) => {
                    const key = r._key;
                    const days = rowDays(r);
                    const multi = days.length > 1;
                    const isSel = selected.has(key);
                    const isNewRow = r._origin === 'new';
                    return (
                      <tr key={key} style={{ background: isSel ? 'rgba(0,115,234,0.06)' : isNewRow ? 'rgba(0,200,117,0.06)' : 'transparent' }}>
                        {isEdit && (
                          <td style={cell()}>
                            <input type="checkbox" checked={isSel} onChange={() => toggleRow(key)} />
                          </td>
                        )}
                        <td style={{ ...cell(), color: '#64748b', whiteSpace: 'nowrap' }}>
                          {r.customerTextId || '—'}
                          {isNewRow && <span style={newTag} title="Kaydedilmemiş yeni müşteri">yeni</span>}
                        </td>
                        <td style={cell()}>
                          <div style={{ fontWeight: 600 }}>{r.customerTitle || '—'}</div>
                          <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{r.address || ''}</div>
                          {/* #808 Distribütör adı ünvanın altında: kapsam dışı müşteriyi
                              listede gözle bulmak imkânsıza yakındı. */}
                          {r.vendorName && (
                            <span
                              style={vendorTag(outsideScopeKeys.has(key))}
                              title={outsideScopeKeys.has(key)
                                ? 'Bu müşteri planın distribütör kapsamı dışında'
                                : 'Müşterinin distribütörü'}
                            >
                              {r.vendorName}
                            </span>
                          )}
                        </td>
                        <td style={{ ...cell(), color: '#475569' }}>
                          {[r.province, r.neighborhood].filter(Boolean).join(' · ') || r.region || '—'}
                        </td>
                        <td style={cell()}>
                          {isEdit && !multi ? (
                            <select style={inlineSelect} value={r.frequency || ''} onChange={(e) => updateSchedule(key, { frequency: e.target.value || null })}>
                              <option value="">—</option>
                              {FREQUENCY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                          ) : <span>{frequencyLabel(r.frequency)}</span>}
                        </td>
                        <td style={cell()}>
                          {isEdit && !multi ? (
                            <select style={{ ...inlineSelect, borderLeft: `3px solid ${days[0] ? dayColor(days[0]) : 'transparent'}` }} value={days[0] ?? ''} onChange={(e) => updateSchedule(key, { dayNo: e.target.value ? Number(e.target.value) : null })}>
                              <option value="">—</option>
                              {DAY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                            </select>
                          ) : <DayBadges days={days} onRemoveDay={isEdit ? (d) => removeDayFromRow(key, d) : null} />}
                        </td>
                        {isEdit && (
                          <td style={{ ...cell(), textAlign: 'right' }}>
                            <button style={removeBtn} onClick={() => removeCustomer(key)} title="Plandan çıkar">×</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p style={{ fontSize: 12, color: 'var(--text-secondary,#9ca3af)', marginTop: 10 }}>
            {filtered.length} / {activeRows.length} müşteri gösteriliyor. Frekans ve gün ataması ziyaret programını otomatik üretir.
            {view === 'list' && <> Çoklu güne atanmış müşteriler rozetlerle gösterilir; değişiklik için yan panelden seçip işleyin.</>}
          </p>
        </div>
      </div>

      {showAdd && (
        <AddCustomersModal
          planId={planId}
          vendorIds={vendorIds}
          stagedCustomerIds={stagedCustomerIds}
          onClose={() => setShowAdd(false)}
          onStage={handleStage}
          showToast={showToast}
        />
      )}
      {orderDay && (
        <VisitOrderModal
          planId={planId}
          day={orderDay}
          customers={orderCustomers}
          onClose={() => setOrderDay(null)}
          onSaved={customers.reload}
          showToast={showToast}
          readOnly={!isEdit}
        />
      )}
    </div>
  );
}

// Bir müşterinin atandığı tüm günleri renkli rozet olarak gösterir.
/**
 * #799.1 Gün rozetlerinin sağ üst köşesine ✕ — müşteriyi SADECE O GÜNDEN çıkarır.
 *
 * Eskiden çoklu-güne atanmış bir müşteriyi tek bir günden çıkarmanın yolu yoktu: satır içi gün
 * select'i çoklu-günde kilitli, yan paneldeki "Günden çıkar" ise TÜM günleri siliyordu. Kullanıcı
 * Pzt+Cuma'daki müşteriyi yalnız Cuma'dan almak isteyince Pzt'yi de kaybediyor, yeniden atamak
 * zorunda kalıyordu.
 *
 * `onRemoveDay` yalnız düzenleme modunda geçilir; salt-okunur/izleme görünümünde rozet sade kalır.
 */
function DayBadges({ days, onRemoveDay }) {
  if (!days || days.length === 0) return <span style={{ color: '#cbd5e1' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {days.map((d) => {
        const color = dayColor(d);
        if (!onRemoveDay) {
          return <span key={d} style={dayBadge(color)} title={dayLabel(d)}>{dayShort(d)}</span>;
        }
        return (
          <span key={d} style={{ ...dayBadge(color), position: 'relative', paddingRight: 9 }} title={dayLabel(d)}>
            {dayShort(d)}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemoveDay(d); }}
              title={`${dayLabel(d)} gününden çıkar`}
              aria-label={`${dayLabel(d)} gününden çıkar`}
              style={dayBadgeRemove(color)}
            >×</button>
          </span>
        );
      })}
    </div>
  );
}

const countPill = {
  fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'rgba(0,115,234,0.1)',
  border: '1px solid rgba(0,115,234,0.2)', padding: '3px 10px', borderRadius: 999,
};
// #189 kaydedilmemiş taslak değişiklik şeridi (amber).
const pendingBanner = {
  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14,
  padding: '10px 14px', borderRadius: 12, fontSize: 13,
  background: 'rgba(253,171,61,0.10)', border: '1px solid rgba(253,171,61,0.34)', color: '#92600a',
};
// #805 kapsam dışı müşteri şeridi (kırmızı — kaydedilmemiş değişiklik şeridinden ayrışsın diye).
const scopeWarnBanner = {
  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14,
  padding: '10px 14px', borderRadius: 12, fontSize: 13,
  background: 'rgba(220,38,38,0.09)', border: '1px solid rgba(220,38,38,0.32)', color: '#b91c1c',
};
// #808 Uyarı şeridindeki toplu temizleme düğmesi — şeridin kırmızısıyla aynı ailede,
// ama dolu zemin: yıkıcı bir işlem olduğu okunur kalsın.
const scopeWarnBtn = {
  border: '1px solid rgba(220,38,38,0.5)', background: 'rgba(220,38,38,0.12)', color: '#b91c1c',
  padding: '5px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 800, cursor: 'pointer',
};
// #808 Müşteri satırındaki distribütör rozeti; kapsam dışıysa kırmızıya döner.
const vendorTag = (outside) => ({
  display: 'inline-block', marginTop: 3, fontSize: 10.5, fontWeight: 700, borderRadius: 999,
  padding: '1px 8px', verticalAlign: 'middle',
  color: outside ? '#b91c1c' : 'var(--text-secondary,#64748b)',
  background: outside ? 'rgba(220,38,38,0.10)' : 'var(--bg-secondary,#f1f5f9)',
  border: `1px solid ${outside ? 'rgba(220,38,38,0.32)' : 'var(--border-color,#e5e7eb)'}`,
});
const newTag = {
  marginLeft: 6, fontSize: 9.5, fontWeight: 800, color: '#15803d', background: 'rgba(0,200,117,0.14)',
  border: '1px solid rgba(0,200,117,0.4)', borderRadius: 999, padding: '1px 6px', verticalAlign: 'middle',
};
const cellHead = (w) => ({
  padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
  color: 'var(--text-secondary,#6b7280)', borderBottom: '1px solid var(--border-color,#e5e7eb)',
  background: 'var(--bg-secondary,#f8fafc)', width: w,
});
const cell = () => ({ padding: '10px 14px', borderBottom: '1px solid var(--border-color,#f1f5f9)', verticalAlign: 'middle' });
const inlineSelect = {
  padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border-color,#d1d5db)',
  background: 'var(--input-bg,#fff)', fontSize: 12.5, width: '100%', outline: 'none',
};
const viewBtn = (active) => ({
  border: 'none', padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 500,
  background: active ? 'var(--accent)' : 'var(--input-bg,#fff)', color: active ? '#fff' : 'var(--text-primary,#1f2937)',
});
const removeBtn = {
  border: '1px solid var(--border-color,#e5e7eb)', background: 'var(--input-bg,#fff)', color: 'var(--danger)',
  width: 26, height: 26, borderRadius: 8, cursor: 'pointer', fontSize: 16, lineHeight: 1, fontWeight: 700,
};
// HEP AÇIK yan toplu-atama paneli.
const sidePanel = {
  position: 'sticky', top: 8, alignSelf: 'flex-start', width: 280, flexShrink: 0,
  display: 'flex', flexDirection: 'column', gap: 10,
  padding: '14px 14px 16px', borderRadius: 14,
  background: 'var(--bg-card,#fff)', border: '1px solid rgba(0,115,234,0.35)',
  boxShadow: '0 6px 20px rgba(0,115,234,0.10)',
};
const panelSection = {
  display: 'flex', flexDirection: 'column',
  padding: '10px 0 12px', borderTop: '1px solid var(--border-color,#eef2f7)',
};
const panelHint = { margin: '0 0 2px', fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary,#64748b)' };
const panelLabel = { fontSize: 11.5, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase', color: 'var(--text-secondary,#475569)' };
const bulkCount = {
  fontSize: 11.5, fontWeight: 800, color: '#fff', background: 'var(--accent)',
  padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap', marginLeft: 'auto',
};
const bulkCountEmpty = {
  fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary,#64748b)', background: 'var(--bg-secondary,#f1f5f9)',
  padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap', marginLeft: 'auto',
};
const dayChip = (on, color) => ({
  minWidth: 36, padding: '5px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
  color: on ? '#fff' : color,
  background: on ? color : `${color}14`,
  border: `1px solid ${on ? color : `${color}55`}`,
});
const dayBadge = (color) => ({
  display: 'inline-flex', alignItems: 'center', minWidth: 30, justifyContent: 'center',
  padding: '3px 7px', borderRadius: 999, fontSize: 11, fontWeight: 700,
  color, background: `${color}18`, border: `1px solid ${color}55`,
});
// #799.1 Rozetin SAĞ ÜST köşesindeki ✕ (talep bu konumu istiyor). Rozetin akışını bozmaması için
// absolute; dokunma hedefi küçük kalmasın diye 14px daire.
const dayBadgeRemove = (color) => ({
  position: 'absolute', top: -6, right: -6, width: 14, height: 14, lineHeight: '12px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: 0, borderRadius: '50%', cursor: 'pointer',
  fontSize: 11, fontWeight: 800, color: '#fff', background: color,
  border: '1.5px solid var(--card, #fff)',
});
